import type { CameraView, PropDef, Vec2, WinterZone } from '../core/types';
import type { Track } from '../game/track';
import { hash01, TAU } from '../core/math';

export interface TrackLayer {
  canvas: HTMLCanvasElement;
  /** világkoordináta, ahova a canvas bal felső sarka esik */
  minX: number;
  minY: number;
  width: number;
  height: number;
  scale: number;
}

const BUILDING_COLORS = ['#6f6152', '#7d6a58', '#5d6570', '#73655a', '#88796a', '#646b74'];
const DESERT_BUILDINGS = ['#a9784e', '#b98a5a', '#c9a06a', '#8f6a4a', '#b17f52'];
const WINDOW_LIT = '#ffd98a';
const WINDOW_DARK = 'rgba(30,36,46,0.55)';
const TREE_GREEN = ['#4f7a4a', '#5f8f55', '#456e43'];
/** a belső ív parkos szakaszai (s 0..1) */
const PARK_ZONES: [number, number][] = [
  [0.1, 0.32],
  [0.55, 0.78],
];

const CITY_ROOFS = ['#343b48', '#3a4150', '#2f3644', '#3d4452'];
const SUBURB_ROOFS = ['#4a5058', '#545a63', '#434952', '#5b616a'];

/** A pályán kívüli terület: várostömbök tetői, hogy ne legyen üres a külváros. */
const drawCityBlocks = (
  ctx: CanvasRenderingContext2D,
  minX: number,
  minY: number,
  w: number,
  h: number,
  roofs: string[],
): void => {
  const bw = 188;
  const bh = 152;
  const cols = Math.ceil(w / bw) + 1;
  const rows = Math.ceil(h / bh) + 1;
  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      const h1 = hash01(i * 7919 + j * 104729);
      if (h1 < 0.14) continue;
      const x = minX + i * bw + 7 + h1 * 9;
      const y = minY + j * bh + 7 + hash01(i * 3 + j * 31) * 9;
      const ww = bw - 26 - hash01(i * 13 + j) * 18;
      const hh = bh - 24 - hash01(i * 17 + j * 7) * 16;
      ctx.fillStyle = roofs[(i * 3 + j * 5) % roofs.length];
      ctx.fillRect(x, y, ww, hh);
      ctx.strokeStyle = 'rgba(255,255,255,0.05)';
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, ww, hh);
      const lit = hash01(i * 31 + j * 17) > 0.55;
      ctx.fillStyle = lit ? 'rgba(255,217,138,0.16)' : 'rgba(0,0,0,0.12)';
      for (let k = 0; k < 3; k++) ctx.fillRect(x + 10 + k * 24, y + hh * 0.52, 8, 8);
    }
  }
};

const tracePath = (ctx: CanvasRenderingContext2D, track: Track): void => {
  const s = track.samples;
  ctx.beginPath();
  ctx.moveTo(s[0].pos.x, s[0].pos.y);
  for (let i = 1; i < s.length; i++) ctx.lineTo(s[i].pos.x, s[i].pos.y);
  ctx.closePath();
};

const polyline = (ctx: CanvasRenderingContext2D, points: Vec2[]): void => {
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
};

/** oldalirányban eltolt, sima vonal a pálya mentén */
const offsetLine = (track: Track, lateral: number, from = 0, to = 1): Vec2[] => {
  const pts: Vec2[] = [];
  const n = 240;
  for (let i = 0; i <= n; i++) pts.push(track.posAt(from + ((to - from) * i) / n, lateral));
  return pts;
};

const drawBuildings = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const tangent = track.tangentAt(prop.s);
  const angle = Math.atan2(tangent.y, tangent.x);
  const scale = prop.scale ?? 1;
  const seed = Math.round(prop.s * 10000) + (prop.lateral > 0 ? 91 : 7);
  const w = (56 + hash01(seed) * 44) * scale;
  const d = (58 + hash01(seed + 3) * 48) * scale;
  const palette = track.def.theme === 'desert' ? DESERT_BUILDINGS : BUILDING_COLORS;
  const color = palette[(prop.variant ?? 0) % palette.length];

  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(angle);

  // talp-árnyék
  ctx.fillStyle = 'rgba(10,12,18,0.32)';
  ctx.fillRect(-w / 2 + 9, -d / 2 + 13, w, d);

  // tető
  ctx.fillStyle = '#2f3644';
  ctx.fillRect(-w / 2, -d / 2, w, d);

  // homlokfal
  ctx.fillStyle = color;
  ctx.fillRect(-w / 2 + 4, -d / 2 + 4, w - 8, d - 8);
  ctx.strokeStyle = 'rgba(16,19,28,0.55)';
  ctx.lineWidth = 2;
  ctx.strokeRect(-w / 2 + 4, -d / 2 + 4, w - 8, d - 8);

  // ablakrács
  const cols = Math.max(2, Math.floor((w - 20) / 17));
  const rows = Math.max(2, Math.floor((d - 20) / 17));
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const lit = hash01(seed + r * 31 + c * 7) > 0.62;
      ctx.fillStyle = lit ? WINDOW_LIT : WINDOW_DARK;
      ctx.fillRect(-w / 2 + 12 + c * 17, -d / 2 + 12 + r * 17, 9, 9);
    }
  }

  // havas témán hóréteg a tetőn
  if (track.def.theme === 'snow') {
    ctx.fillStyle = 'rgba(240,250,255,0.92)';
    ctx.fillRect(-w / 2 + 2, -d / 2 + 2, w - 4, 11);
    ctx.fillStyle = 'rgba(200,225,245,0.85)';
    ctx.fillRect(-w / 2 + 2, -d / 2 + 2, w - 4, 4);
  }

  // tető-perem
  ctx.strokeStyle = 'rgba(255,236,200,0.22)';
  ctx.lineWidth = 2;
  ctx.strokeRect(-w / 2 + 2, -d / 2 + 2, w - 4, d - 4);
  ctx.restore();
};

const drawTree = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const scale = prop.scale ?? 1;
  const seed = Math.round(prop.s * 9000) + 13;
  const r = (16 + hash01(seed) * 9) * scale;
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.fillStyle = 'rgba(10,12,18,0.3)';
  ctx.beginPath();
  ctx.ellipse(5, 7, r * 0.9, r * 0.6, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = '#4a3a2c';
  ctx.fillRect(-3, -3, 6, 10);
  const green = TREE_GREEN[(prop.variant ?? 0) % TREE_GREEN.length];
  ctx.fillStyle = green;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, TAU);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.13)';
  ctx.beginPath();
  ctx.arc(-r * 0.28, -r * 0.3, r * 0.55, 0, TAU);
  ctx.fill();
  ctx.restore();
};

const drawLamp = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const tangent = track.tangentAt(prop.s);
  const inward = Math.sign(prop.lateral) * -1;
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(Math.atan2(tangent.y, tangent.x));
  ctx.strokeStyle = '#2c313b';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(0, inward * 26);
  ctx.stroke();
  ctx.fillStyle = '#ffd98a';
  ctx.beginPath();
  ctx.arc(0, inward * 26, 6, 0, TAU);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,217,138,0.18)';
  ctx.beginPath();
  ctx.arc(0, inward * 26, 16, 0, TAU);
  ctx.fill();
  ctx.restore();
};

const drawKiosk = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const tangent = track.tangentAt(prop.s);
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(Math.atan2(tangent.y, tangent.x));
  ctx.fillStyle = 'rgba(10,12,18,0.3)';
  ctx.fillRect(-20, -14, 44, 30);
  ctx.fillStyle = '#e2d3b4';
  ctx.fillRect(-24, -18, 48, 34);
  ctx.fillStyle = '#c9543f';
  for (let i = 0; i < 4; i++) ctx.fillRect(-22 + i * 12, -16, 6, 30);
  ctx.fillStyle = '#3b4250';
  ctx.fillRect(-24, 8, 48, 6);
  ctx.restore();
};

const drawBench = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const tangent = track.tangentAt(prop.s);
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(Math.atan2(tangent.y, tangent.x));
  ctx.fillStyle = '#6b4f36';
  ctx.fillRect(-16, -7, 32, 6);
  ctx.fillStyle = '#4a3a2c';
  ctx.fillRect(-15, 3, 3, 5);
  ctx.fillRect(12, 3, 3, 5);
  ctx.restore();
};

const drawCone = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.fillStyle = '#e8792f';
  ctx.beginPath();
  ctx.moveTo(0, -11);
  ctx.lineTo(9, 9);
  ctx.lineTo(-9, 9);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#f4f1e8';
  ctx.fillRect(-6, 0, 12, 4);
  ctx.restore();
};

const drawParkedCar = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const tangent = track.tangentAt(prop.s);
  const palette = ['#8c5f5f', '#5f6f8c', '#6f8c6a', '#8c8360'];
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(Math.atan2(tangent.y, tangent.x));
  ctx.fillStyle = 'rgba(10,12,18,0.28)';
  ctx.fillRect(-23, -11, 50, 26);
  ctx.fillStyle = palette[(prop.variant ?? 0) % palette.length];
  ctx.fillRect(-26, -13, 52, 26);
  ctx.fillStyle = '#a8d4e2';
  ctx.fillRect(-8, -10, 16, 20);
  ctx.fillStyle = '#1a1c22';
  ctx.fillRect(-20, -15, 12, 4);
  ctx.fillRect(-20, 11, 12, 4);
  ctx.fillRect(10, -15, 12, 4);
  ctx.fillRect(10, 11, 12, 4);
  ctx.restore();
};

const drawSign = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.fillStyle = '#2c313b';
  ctx.fillRect(-2, -20, 4, 22);
  ctx.fillStyle = prop.variant === 1 ? '#3ec6c0' : prop.variant === 2 ? '#ffc857' : '#f4f1e8';
  ctx.beginPath();
  ctx.arc(0, -24, 9, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = '#2c313b';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
};

const drawSmallProp = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  ctx.save();
  ctx.translate(pos.x, pos.y);
  if (prop.kind === 'hydrant') {
    ctx.fillStyle = '#c9543f';
    ctx.fillRect(-5, -10, 10, 16);
    ctx.fillStyle = '#e8792f';
    ctx.fillRect(-8, -4, 16, 5);
  } else if (prop.kind === 'bin') {
    ctx.fillStyle = '#4a5563';
    ctx.fillRect(-7, -9, 14, 18);
    ctx.fillStyle = '#5f6b7a';
    ctx.fillRect(-8, -12, 16, 4);
  }
  ctx.restore();
};

const drawCrosswalk = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, 0);
  const tangent = track.tangentAt(prop.s);
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(Math.atan2(tangent.y, tangent.x));
  ctx.fillStyle = 'rgba(244,241,232,0.86)';
  const half = track.def.roadHalfWidth - 8;
  for (let lat = -half; lat < half; lat += 24) {
    ctx.fillRect(-14, lat, 28, 13);
  }
  ctx.restore();
};

const drawArrow = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const tangent = track.tangentAt(prop.s);
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(Math.atan2(tangent.y, tangent.x));
  // festett sávnyíl: fehér, egyetlen nyílhegy – nem téveszthető össze a boost-pad sárga chevronjaival
  ctx.fillStyle = 'rgba(244,241,232,0.28)';
  ctx.beginPath();
  ctx.moveTo(20, 0);
  ctx.lineTo(-2, -13);
  ctx.lineTo(-2, -5);
  ctx.lineTo(-20, -5);
  ctx.lineTo(-20, 5);
  ctx.lineTo(-2, 5);
  ctx.lineTo(-2, 13);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
};

const drawTram = (ctx: CanvasRenderingContext2D, track: Track): void => {
  const L = track.totalLength;
  const from = 0.455;
  const to = 0.545;
  ctx.save();
  for (const lateral of [-19, 19]) {
    const pts: Vec2[] = [];
    const steps = 90;
    for (let i = 0; i <= steps; i++) pts.push(track.posAt(from + ((to - from) * i) / steps, lateral));
    polyline(ctx, pts);
    ctx.strokeStyle = 'rgba(28,30,36,0.55)';
    ctx.lineWidth = 6;
    ctx.stroke();
    polyline(ctx, pts);
    ctx.strokeStyle = 'rgba(190,205,220,0.55)';
    ctx.lineWidth = 2;
    ctx.stroke();
  }
  // talpfák
  for (let s = from * L; s < to * L; s += 22) {
    const p = track.posAt(s / L, 0);
    const t = track.tangentAt(s / L);
    const a = Math.atan2(t.y, t.x);
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(a);
    ctx.fillStyle = 'rgba(40,38,34,0.32)';
    ctx.fillRect(-3, -28, 6, 56);
    ctx.restore();
  }
  ctx.restore();
};

const drawStartLine = (ctx: CanvasRenderingContext2D, track: Track): void => {
  const progress = track.startS / track.totalLength;
  const pos = track.posAt(progress, 0);
  const tangent = track.tangentAt(progress);
  const half = track.def.roadHalfWidth;
  const cols = 12;
  const cell = (half * 2) / cols;
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(Math.atan2(tangent.y, tangent.x));
  for (let row = 0; row < 2; row++) {
    for (let c = 0; c < cols; c++) {
      ctx.fillStyle = (row + c) % 2 === 0 ? '#f4f1e8' : '#20242c';
      ctx.fillRect(-20 + row * 20, -half + c * cell, 20, cell);
    }
  }
  ctx.font = '700 26px "Trebuchet MS", sans-serif';
  ctx.fillStyle = 'rgba(244,241,232,0.75)';
  ctx.textAlign = 'center';
  ctx.save();
  ctx.translate(70, 0);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText('START', 0, 8);
  ctx.restore();
  ctx.restore();
};

const drawPitLane = (ctx: CanvasRenderingContext2D, track: Track): void => {
  const from = 0.006;
  const to = 0.158;
  const pts = offsetLine(track, -190, from, to);
  if (pts.length < 2) return;
  polyline(ctx, pts);
  ctx.lineCap = 'butt';
  ctx.strokeStyle = '#3b4048';
  ctx.lineWidth = 64;
  ctx.stroke();
  ctx.strokeStyle = 'rgba(244,241,232,0.75)';
  ctx.lineWidth = 3;
  for (const lateral of [-159, -221]) {
    polyline(ctx, offsetLine(track, lateral, from, to));
    ctx.stroke();
  }
};

const drawGrandstand = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const tan = track.tangentAt(prop.s);
  const angle = Math.atan2(tan.y, tan.x);
  const scale = prop.scale ?? 1;
  const len = 150 * scale;
  const width = 58 * scale;
  const stripe = prop.variant === 1 ? '#3ec6c0' : prop.variant === 2 ? '#ffc857' : '#ff5c4d';

  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(angle);
  // alap + lépcsős nézőtér
  ctx.fillStyle = '#1a1f26';
  ctx.fillRect(-len / 2, -width / 2, len, width);
  for (let i = 0; i < 4; i++) {
    ctx.fillStyle = i % 2 === 0 ? 'rgba(244,241,232,0.16)' : stripe;
    ctx.globalAlpha = i % 2 === 0 ? 1 : 0.55;
    ctx.fillRect(-len / 2 + 4, -width / 2 + 4 + i * ((width - 8) / 4), len - 8, (width - 8) / 4 - 3);
  }
  ctx.globalAlpha = 1;
  // tető
  ctx.fillStyle = 'rgba(12,16,22,0.85)';
  ctx.fillRect(-len / 2 - 6, -width / 2 - 8, len + 12, 8);
  // nézők
  for (let i = 0; i < 60; i++) {
    const x = -len / 2 + 6 + hash01(prop.s * 1000 + i * 7) * (len - 12);
    const y = -width / 2 + 6 + hash01(prop.s * 700 + i * 13) * (width - 12);
    ctx.fillStyle = hash01(i * 3 + prop.s * 100) > 0.5 ? 'rgba(255,255,255,0.5)' : 'rgba(255,200,120,0.45)';
    ctx.fillRect(x, y, 2, 2);
  }
  ctx.restore();
};

const drawTyreWall = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const tan = track.tangentAt(prop.s);
  const angle = Math.atan2(tan.y, tan.x);
  const scale = prop.scale ?? 1;
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(angle);
  for (let i = 0; i < 5; i++) {
    const x = (i - 2) * 13 * scale;
    ctx.fillStyle = i % 2 === 0 ? '#15181d' : '#f4f1e8';
    ctx.beginPath();
    ctx.arc(x, 0, 7 * scale, 0, TAU);
    ctx.fill();
    ctx.fillStyle = i % 2 === 0 ? '#e10600' : '#c9c4b8';
    ctx.beginPath();
    ctx.arc(x, 0, 4.2 * scale, 0, TAU);
    ctx.fill();
  }
  ctx.restore();
};

const drawPitBuilding = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const tan = track.tangentAt(prop.s);
  const angle = Math.atan2(tan.y, tan.x);
  const scale = prop.scale ?? 1;
  const len = 150 * scale;
  const depth = 46 * scale;
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(angle);
  ctx.fillStyle = '#20252d';
  ctx.fillRect(-len / 2, -depth / 2, len, depth);
  ctx.fillStyle = 'rgba(255,255,255,0.08)';
  ctx.fillRect(-len / 2, -depth / 2, len, 8);
  // boxkapuk a pálya felé eső oldalon
  for (let i = 0; i < 3; i++) {
    ctx.fillStyle = i === 1 ? 'rgba(255,214,140,0.5)' : 'rgba(180,200,220,0.32)';
    ctx.fillRect(-len / 2 + 14 + i * 44, depth / 2 - 10, 32, 8);
  }
  ctx.restore();
};

const drawFlagpole = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const scale = prop.scale ?? 1;
  const color = prop.variant === 1 ? '#3ec6c0' : prop.variant === 2 ? '#ffc857' : '#e10600';
  ctx.save();
  ctx.strokeStyle = 'rgba(230,235,240,0.8)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(pos.x, pos.y);
  ctx.lineTo(pos.x, pos.y - 26 * scale);
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(pos.x, pos.y - 26 * scale);
  ctx.lineTo(pos.x + 16 * scale, pos.y - 20 * scale);
  ctx.lineTo(pos.x, pos.y - 14 * scale);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
};

/** Hungaroring: fűnyírás-csíkok a gyepen. */
const drawMowingStripes = (ctx: CanvasRenderingContext2D, minX: number, minY: number, w: number, h: number): void => {
  ctx.save();
  ctx.beginPath();
  ctx.rect(minX, minY, w, h);
  ctx.clip();
  ctx.translate(minX + w / 2, minY + h / 2);
  ctx.rotate(-0.5);
  const span = Math.hypot(w, h);
  for (let x = -span; x < span; x += 132) {
    ctx.fillStyle = 'rgba(255,255,255,0.045)';
    ctx.fillRect(x, -span, 66, span * 2);
  }
  ctx.restore();
};

/** Hungaroring: kavicságyak a kanyarok külső ívén. */
const drawGravelTraps = (ctx: CanvasRenderingContext2D, track: Track): void => {
  const zones: [number, number][] = [
    [0.16, 0.21],
    [0.27, 0.315],
    [0.37, 0.42],
    [0.51, 0.56],
    [0.63, 0.675],
    [0.75, 0.795],
    [0.86, 0.905],
  ];
  for (const [from, to] of zones) {
    polyline(ctx, offsetLine(track, 188, from, to));
    ctx.lineCap = 'butt';
    ctx.strokeStyle = '#cbb78d';
    ctx.lineWidth = 86;
    ctx.stroke();
    ctx.strokeStyle = 'rgba(255,246,220,0.55)';
    ctx.lineWidth = 3;
    polyline(ctx, offsetLine(track, 146, from, to));
    ctx.stroke();
    // kavics-szemcse
    for (let i = 0; i < 30; i++) {
      const s = from + ((to - from) * i) / 30;
      const p = track.posAt(s, 188 + (hash01(i * 7 + from * 100) * 2 - 1) * 32);
      ctx.fillStyle = hash01(i * 13 + from * 50) > 0.5 ? 'rgba(90,74,52,0.25)' : 'rgba(255,250,235,0.3)';
      ctx.fillRect(p.x, p.y, 2.5, 2.5);
    }
  }
};

/** Hungaroring: festett rajtkockák a célegyenesen. */
const drawGridSlots = (ctx: CanvasRenderingContext2D, track: Track): void => {
  const len = track.totalLength;
  const base = track.def.startS * len - 90;
  ctx.save();
  ctx.strokeStyle = 'rgba(244,241,232,0.75)';
  ctx.lineWidth = 3;
  for (let i = 0; i < 6; i++) {
    const row = Math.floor(i / 2);
    const s = (base - row * 74) / len;
    const lateral = i % 2 === 0 ? -54 : 54;
    const pos = track.posAt(s, lateral);
    const tan = track.tangentAt(s);
    const angle = Math.atan2(tan.y, tan.x);
    ctx.save();
    ctx.translate(pos.x, pos.y);
    ctx.rotate(angle);
    // U alak, nyitva a haladás irányába (+x): a kocsi előre hajt ki belőle
    ctx.beginPath();
    ctx.moveTo(30, -24);
    ctx.lineTo(-30, -24);
    ctx.lineTo(-30, 24);
    ctx.lineTo(30, 24);
    ctx.stroke();
    ctx.restore();
  }
  ctx.restore();
};

/** Hungaroring: startgantry a rajtvonal előtt. */
const drawStartGantry = (ctx: CanvasRenderingContext2D, track: Track): void => {
  const len = track.totalLength;
  const s = (track.def.startS * len + 10) / len;
  const pos = track.posAt(s, 0);
  const tan = track.tangentAt(s);
  const angle = Math.atan2(tan.y, tan.x);
  const half = track.def.roadHalfWidth + 34;

  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(angle);
  // lábak a pálya két oldalán
  ctx.fillStyle = '#20242b';
  ctx.fillRect(-9, -half - 12, 18, 12);
  ctx.fillRect(-9, half, 18, 12);
  // gerenda a pálya fölött (keresztben)
  ctx.fillStyle = '#181c22';
  ctx.fillRect(-6, -half, 12, half * 2);
  ctx.fillStyle = '#e10600';
  ctx.fillRect(-7, -half + 10, 6, half * 2 - 20);
  // felirat a gerendán, a pálya irányában
  ctx.save();
  ctx.rotate(-Math.PI / 2);
  ctx.fillStyle = '#f4f1e8';
  ctx.font = '700 9px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('HUNGARORING', 0, 0);
  ctx.restore();
  // rajtlámpák a gerenda közepén
  for (let i = 0; i < 5; i++) {
    ctx.fillStyle = i < 4 ? '#ff2d2d' : '#3a3f46';
    ctx.beginPath();
    ctx.arc(-16 + i * 8, 0, 3.2, 0, TAU);
    ctx.fill();
  }
  ctx.restore();
};

/** Hungaroring: boxfal, garázsok, irányítótorony. */
const drawPitComplex = (ctx: CanvasRenderingContext2D, track: Track): void => {
  const len = track.totalLength;
  const from = 0.006;
  const to = 0.158;

  // boxfal a pálya és a boxutca között
  polyline(ctx, offsetLine(track, -150, from, to));
  ctx.lineCap = 'butt';
  ctx.strokeStyle = '#d9d6cc';
  ctx.lineWidth = 5;
  ctx.stroke();

  // garázsok a boxutca túloldalán
  const colors = ['#e10600', '#00d2be', '#ff8700', '#005aff', '#dc0000', '#900000', '#ffffff', '#14c04b', '#ff87bc'];
  for (let i = 0; i < 9; i++) {
    const s = 0.012 + i * 0.0155;
    const pos = track.posAt(s, -262);
    const tan = track.tangentAt(s);
    const angle = Math.atan2(tan.y, tan.x);
    ctx.save();
    ctx.translate(pos.x, pos.y);
    ctx.rotate(angle);
    ctx.fillStyle = '#20242b';
    ctx.fillRect(-22, -20, 44, 40);
    ctx.fillStyle = colors[i % colors.length];
    ctx.fillRect(-20, -18, 40, 10);
    ctx.fillStyle = 'rgba(255,255,255,0.16)';
    ctx.fillRect(-20, 4, 40, 14);
    ctx.restore();
  }

  // irányítótorony a depó fölött
  const towerS = 0.03;
  const tower = track.posAt(towerS, -430);
  const tan = track.tangentAt(towerS);
  ctx.save();
  ctx.translate(tower.x, tower.y);
  ctx.rotate(Math.atan2(tan.y, tan.x));
  ctx.fillStyle = '#39424c';
  ctx.fillRect(-24, -34, 48, 68);
  ctx.fillStyle = 'rgba(190,220,240,0.5)';
  for (let i = 0; i < 4; i++) {
    for (let j = 0; j < 2; j++) ctx.fillRect(-18 + j * 26, -26 + i * 14, 10, 7);
  }
  ctx.fillStyle = '#c1121f';
  ctx.fillRect(-24, -34, 48, 6);
  ctx.restore();
};

/** Hungaroring: szponzortáblák és féktáblák a pálya szélén. */
const drawCircuitBoards = (ctx: CanvasRenderingContext2D, track: Track): void => {
  const colors = ['#e10600', '#00d2be', '#ff8700', '#005aff', '#ffc857'];
  for (let i = 0; i < 26; i++) {
    const s = (i + 0.25) / 26;
    const pos = track.posAt(s, 152);
    const tan = track.tangentAt(s);
    ctx.save();
    ctx.translate(pos.x, pos.y);
    ctx.rotate(Math.atan2(tan.y, tan.x));
    ctx.fillStyle = colors[i % colors.length];
    ctx.globalAlpha = 0.85;
    ctx.fillRect(-26, -4, 52, 9);
    ctx.globalAlpha = 1;
    ctx.restore();
  }
  // féktáblák a T1 előtt
  for (const [s, label] of [
    [0.115, '100'],
    [0.13, '50'],
  ] as [number, string][]) {
    const pos = track.posAt(s, 158);
    ctx.save();
    ctx.translate(pos.x, pos.y);
    ctx.fillStyle = '#f4f1e8';
    ctx.fillRect(-11, -8, 22, 16);
    ctx.fillStyle = '#111';
    ctx.font = '800 8px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, 0, 1);
    ctx.restore();
  }
};

/** Sivatagi homok, dűnék és por. */
const drawDesertGround = (
  ctx: CanvasRenderingContext2D,
  minX: number,
  minY: number,
  w: number,
  h: number,
): void => {
  ctx.save();
  for (let i = 0; i < 16; i++) {
    const y = minY + (i + 0.5) * (h / 16);
    const off = hash01(i * 13 + 3) * 70;
    ctx.beginPath();
    ctx.moveTo(minX, y + off);
    ctx.bezierCurveTo(minX + w * 0.25, y - 45 + (i % 3) * 22, minX + w * 0.62, y + 70, minX + w, y + off * 0.3);
    ctx.strokeStyle = i % 2 === 0 ? 'rgba(150,110,60,0.12)' : 'rgba(255,235,190,0.08)';
    ctx.lineWidth = 18 + (i % 4) * 9;
    ctx.stroke();
  }
  for (let i = 0; i < 1500; i++) {
    const x = minX + hash01(i * 7 + 1) * w;
    const y = minY + hash01(i * 13 + 5) * h;
    const size = 1 + hash01(i * 17 + 3) * 2.2;
    ctx.fillStyle = hash01(i + 2) > 0.5 ? 'rgba(120,88,50,0.16)' : 'rgba(255,240,200,0.12)';
    ctx.fillRect(x, y, size, size);
  }
  ctx.restore();
};

/** Havas tundra: hófúvások, csillogó hószemcsék, távoli fenyvesfoltok. */
const drawSnowGround = (
  ctx: CanvasRenderingContext2D,
  minX: number,
  minY: number,
  w: number,
  h: number,
): void => {
  ctx.save();
  for (let i = 0; i < 14; i++) {
    const y = minY + (i + 0.5) * (h / 14);
    const off = hash01(i * 17 + 5) * 90;
    ctx.beginPath();
    ctx.moveTo(minX, y + off);
    ctx.bezierCurveTo(minX + w * 0.3, y - 40 + (i % 3) * 18, minX + w * 0.66, y + 60, minX + w, y + off * 0.4);
    ctx.strokeStyle = i % 2 === 0 ? 'rgba(160,195,225,0.20)' : 'rgba(255,255,255,0.22)';
    ctx.lineWidth = 26 + (i % 4) * 12;
    ctx.stroke();
  }
  for (let i = 0; i < 1600; i++) {
    const x = minX + hash01(i * 7 + 1) * w;
    const y = minY + hash01(i * 13 + 5) * h;
    const size = 1 + hash01(i * 17 + 3) * 2.4;
    ctx.fillStyle = hash01(i + 2) > 0.5 ? 'rgba(255,255,255,0.5)' : 'rgba(150,190,220,0.25)';
    ctx.fillRect(x, y, size, size);
  }
  for (let i = 0; i < 90; i++) {
    const x = minX + hash01(i * 31 + 7) * w;
    const y = minY + hash01(i * 53 + 3) * h;
    const r = 4 + hash01(i * 11) * 7;
    ctx.fillStyle = 'rgba(45,80,70,0.18)';
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.fill();
  }
  // befagyott tavak: világos jégtábla repedésekkel
  for (let i = 0; i < 2; i++) {
    const cx = minX + hash01(i * 97 + 13) * w;
    const cy = minY + hash01(i * 53 + 71) * h;
    const rx = 220 + hash01(i * 7 + 3) * 160;
    const ry = rx * (0.5 + hash01(i * 11 + 5) * 0.2);
    ctx.fillStyle = 'rgba(178,214,236,0.55)';
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0.3, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = 'rgba(240,250,255,0.7)';
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.strokeStyle = 'rgba(255,255,255,0.5)';
    ctx.lineWidth = 1.8;
    for (let k = 0; k < 4; k++) {
      const a = hash01(i * 17 + k * 7) * TAU;
      const len = rx * (0.3 + hash01(i * 23 + k) * 0.5);
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(a) * len, cy + Math.sin(a) * len * 0.6);
      ctx.stroke();
    }
  }
  ctx.restore();
};

/** Hókupac / hóátfúvás a pályán (dinamikusan, versenyenként). */
export const drawWinterZones = (
  ctx: CanvasRenderingContext2D,
  zones: WinterZone[],
  cam: CameraView,
  time: number,
): void => {
  for (const zone of zones) {
    const p = zone.pos;
    if (
      p.x < cam.bounds.minX - 200 ||
      p.x > cam.bounds.maxX + 200 ||
      p.y < cam.bounds.minY - 200 ||
      p.y > cam.bounds.maxY + 200
    ) {
      continue;
    }
    const r = zone.radius;
    ctx.save();
    if (zone.kind === 'snowdrift') {
      ctx.fillStyle = 'rgba(110,150,190,0.42)';
      ctx.beginPath();
      ctx.ellipse(p.x + 7, p.y + 8, r * 1.08, r * 0.74, 0, 0, TAU);
      ctx.fill();
      ctx.fillStyle = '#f7fcff';
      ctx.beginPath();
      ctx.ellipse(p.x, p.y, r, r * 0.68, 0, 0, TAU);
      ctx.fill();
      // kék árnyalat az alján, csúcs a tetején
      ctx.fillStyle = 'rgba(190,218,242,0.7)';
      ctx.beginPath();
      ctx.ellipse(p.x, p.y + r * 0.24, r * 0.62, r * 0.3, 0, 0, TAU);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.ellipse(p.x - r * 0.24, p.y - r * 0.16, r * 0.56, r * 0.32, 0, 0, TAU);
      ctx.fill();
      // kontúr, hogy a hóháton is látszódjon
      ctx.strokeStyle = 'rgba(120,165,205,0.9)';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.ellipse(p.x, p.y, r, r * 0.68, 0, 0, TAU);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(255,255,255,0.8)';
      ctx.lineWidth = 2;
      for (let i = 0; i < 3; i++) {
        const ox = (hash01(zone.id * 13 + i) - 0.5) * r * 1.1;
        const oy = (hash01(zone.id * 29 + i) - 0.5) * r * 0.5;
        ctx.beginPath();
        ctx.moveTo(p.x + ox - r * 0.3, p.y + oy);
        ctx.quadraticCurveTo(p.x + ox, p.y + oy - 6, p.x + ox + r * 0.3, p.y + oy);
        ctx.stroke();
      }
    } else {
      const shimmer = 0.34 + Math.sin(time * 2.1 + zone.id * 1.7) * 0.07;
      ctx.fillStyle = `rgba(150,215,240,${shimmer.toFixed(3)})`;
      ctx.beginPath();
      ctx.ellipse(p.x, p.y, r, r * 0.7, 0.18, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = 'rgba(235,250,255,0.72)';
      ctx.lineWidth = 2.5;
      ctx.stroke();
      ctx.strokeStyle = 'rgba(210,240,255,0.6)';
      ctx.lineWidth = 1.6;
      for (let i = 0; i < 3; i++) {
        const a = hash01(zone.id * 7 + i) * TAU;
        const len = r * (0.35 + hash01(zone.id * 11 + i) * 0.5);
        ctx.beginPath();
        ctx.moveTo(p.x - Math.cos(a) * len * 0.4, p.y - Math.sin(a) * len * 0.4);
        ctx.lineTo(p.x + Math.cos(a) * len, p.y + Math.sin(a) * len);
        ctx.stroke();
      }
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.beginPath();
      ctx.ellipse(p.x - r * 0.3, p.y - r * 0.24, r * 0.34, r * 0.14, -0.3, 0, TAU);
      ctx.fill();
    }
    ctx.restore();
  }
};

/** Befagyott tó a havas pálya belső mezőjén (a belső sokszögre vágva). */
const drawFrozenLake = (ctx: CanvasRenderingContext2D, infield: Vec2[]): void => {
  if (infield.length < 3) return;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of infield) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const rx = Math.max(120, (maxX - minX) * 0.34);
  const ry = Math.max(90, (maxY - minY) * 0.34);

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(infield[0].x, infield[0].y);
  for (let i = 1; i < infield.length; i++) ctx.lineTo(infield[i].x, infield[i].y);
  ctx.closePath();
  ctx.clip();

  const grad = ctx.createRadialGradient(cx - rx * 0.2, cy - ry * 0.35, rx * 0.1, cx, cy, Math.max(rx, ry));
  grad.addColorStop(0, '#e2f5ff');
  grad.addColorStop(0.6, '#a9d6ec');
  grad.addColorStop(1, '#7fb8d8');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0.18, 0, TAU);
  ctx.fill();

  // hóval borított part
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 9;
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0.18, 0, TAU);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(110,160,195,0.55)';
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx + 6, ry + 6, 0.18, 0, TAU);
  ctx.stroke();

  // repedések a jégen
  ctx.strokeStyle = 'rgba(255,255,255,0.6)';
  ctx.lineWidth = 2.4;
  for (let i = 0; i < 7; i++) {
    const a = hash01(i * 17 + 3) * TAU;
    const len = Math.max(rx, ry) * (0.35 + hash01(i * 29 + 5) * 0.55);
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    const mx = cx + Math.cos(a + 0.18) * len * 0.55;
    const my = cy + Math.sin(a + 0.18) * len * 0.55;
    ctx.lineTo(mx, my);
    ctx.lineTo(cx + Math.cos(a) * len, cy + Math.sin(a) * len * 0.72);
    ctx.stroke();
  }

  // hófúvás-foltok a jég szélén
  for (let i = 0; i < 5; i++) {
    const a = hash01(i * 41 + 9) * TAU;
    const d = 0.62 + hash01(i * 13 + 1) * 0.25;
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.beginPath();
    ctx.ellipse(cx + Math.cos(a) * rx * d, cy + Math.sin(a) * ry * d, 22 + hash01(i * 7) * 26, 9 + hash01(i * 5) * 12, a, 0, TAU);
    ctx.fill();
  }
  ctx.restore();
};

/** Kaktusz a sivatagi pálya szélén. */
const drawCactus = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {  const pos = track.posAt(prop.s, prop.lateral);
  const scale = prop.scale ?? 1;
  const seed = Math.round(prop.s * 9000) + 31;
  const height = (26 + hash01(seed) * 20) * scale;
  const width = (8 + hash01(seed + 2) * 4) * scale;
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.fillStyle = 'rgba(80,55,25,0.28)';
  ctx.beginPath();
  ctx.ellipse(5, 7, width * 1.7, width * 0.8, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = '#4f7a45';
  ctx.fillRect(-width / 2, -height, width, height);
  ctx.fillStyle = '#5f8f52';
  ctx.fillRect(-width / 2 + 1.5, -height + 2, width * 0.35, height - 4);
  const armY = -height * (0.48 + hash01(seed + 4) * 0.15);
  ctx.fillStyle = '#4f7a45';
  ctx.fillRect(-width * 1.8, armY, width * 1.4, width * 0.8);
  ctx.fillRect(-width * 1.8, armY - height * 0.22, width * 0.8, height * 0.22);
  ctx.fillRect(width * 0.4, armY + 6, width * 1.4, width * 0.8);
  ctx.fillRect(width * 1.0, armY - height * 0.15, width * 0.8, height * 0.15 + 6);
  ctx.strokeStyle = 'rgba(190,220,160,0.35)';
  ctx.lineWidth = 1;
  for (let i = 0; i < 4; i++) {
    ctx.beginPath();
    ctx.moveTo(-width * 0.2, -height + 4 + i * 7);
    ctx.lineTo(width * 0.4, -height + 6 + i * 7);
    ctx.stroke();
  }
  ctx.restore();
};

/** Sivatagi szikla / homokkő. */
const drawRock = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const scale = prop.scale ?? 1;
  const seed = Math.round(prop.s * 12000) + 73;
  const r = (13 + hash01(seed) * 12) * scale;
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.fillStyle = 'rgba(75,50,25,0.3)';
  ctx.beginPath();
  ctx.ellipse(5, 6, r * 1.4, r * 0.65, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = '#a97c50';
  ctx.beginPath();
  ctx.moveTo(-r, 2);
  ctx.lineTo(-r * 0.55, -r * 0.7);
  ctx.lineTo(r * 0.2, -r);
  ctx.lineTo(r, -r * 0.35);
  ctx.lineTo(r * 0.7, r * 0.45);
  ctx.lineTo(-r * 0.4, r * 0.55);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = 'rgba(240,205,155,0.34)';
  ctx.beginPath();
  ctx.moveTo(-r * 0.55, -r * 0.65);
  ctx.lineTo(r * 0.1, -r * 0.9);
  ctx.lineTo(r * 0.5, -r * 0.4);
  ctx.lineTo(-r * 0.25, -r * 0.2);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
};

/** Régi bányatorony / szállítóállvány. */
const drawMineTower = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const tangent = track.tangentAt(prop.s);
  const scale = prop.scale ?? 1;
  const w = 46 * scale;
  const h = 82 * scale;
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.rotate(Math.atan2(tangent.y, tangent.x));
  ctx.fillStyle = 'rgba(60,40,20,0.3)';
  ctx.beginPath();
  ctx.ellipse(6, 8, w * 0.9, 14 * scale, 0, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = '#5a402b';
  ctx.lineWidth = 5 * scale;
  ctx.beginPath();
  ctx.moveTo(-w / 2, 10);
  ctx.lineTo(-w * 0.22, -h);
  ctx.moveTo(w / 2, 10);
  ctx.lineTo(w * 0.22, -h);
  ctx.stroke();
  ctx.strokeStyle = '#785738';
  ctx.lineWidth = 2.2 * scale;
  for (let i = 0; i < 3; i++) {
    const y = 4 - i * (h / 3.4);
    ctx.beginPath();
    ctx.moveTo(-w / 2 + i * 4, y);
    ctx.lineTo(w / 2 - i * 4, y);
    ctx.stroke();
  }
  ctx.fillStyle = '#4a3524';
  ctx.fillRect(-w * 0.62, -h - 4 * scale, w * 1.24, 7 * scale);
  // bányakerekek
  ctx.strokeStyle = '#6b4c2f';
  ctx.lineWidth = 3 * scale;
  for (const x of [-w * 0.42, w * 0.42]) {
    ctx.beginPath();
    ctx.arc(x, -h - 12 * scale, 8 * scale, 0, TAU);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x - 6 * scale, -h - 12 * scale);
    ctx.lineTo(x + 6 * scale, -h - 12 * scale);
    ctx.moveTo(x, -h - 18 * scale);
    ctx.lineTo(x, -h - 6 * scale);
    ctx.stroke();
  }
  ctx.restore();
};

/** Kátyúk az aszfalton. */
const drawPotholes = (ctx: CanvasRenderingContext2D, track: Track): void => {
  const holes = track.def.potholes;
  if (!holes || holes.length === 0) return;
  for (const hole of holes) {
    const pos = track.posAt(hole.s, hole.lateral);
    const tangent = track.tangentAt(hole.s);
    ctx.save();
    ctx.translate(pos.x, pos.y);
    ctx.rotate(Math.atan2(tangent.y, tangent.x));
    ctx.fillStyle = 'rgba(30,24,18,0.5)';
    ctx.beginPath();
    ctx.ellipse(0, 0, hole.radius * 1.12, hole.radius * 0.85, 0, 0, TAU);
    ctx.fill();
    ctx.fillStyle = '#211b16';
    ctx.beginPath();
    ctx.ellipse(0, 0, hole.radius, hole.radius * 0.72, 0, 0, TAU);
    ctx.fill();
    ctx.fillStyle = '#100d0a';
    ctx.beginPath();
    ctx.ellipse(-1, 1, hole.radius * 0.7, hole.radius * 0.5, 0, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = 'rgba(20,15,12,0.65)';
    ctx.lineWidth = 2;
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + 0.4;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * hole.radius * 0.8, Math.sin(a) * hole.radius * 0.55);
      ctx.lineTo(Math.cos(a) * hole.radius * 1.25, Math.sin(a) * hole.radius * 0.9);
      ctx.stroke();
    }
    ctx.fillStyle = 'rgba(200,170,120,0.25)';
    for (let i = 0; i < 3; i++) {
      ctx.fillRect(-hole.radius * 0.6 + i * 8, hole.radius * 0.65, 3, 2);
    }
    ctx.restore();
  }
};

/** Hófenyő: sötétzöld, hóval borított szintek (felülnézetből). */
const drawPine = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const scale = prop.scale ?? 1;
  const seed = Math.round(prop.s * 9000) + 41;
  const height = (46 + hash01(seed) * 34) * scale;
  const width = (14 + hash01(seed + 2) * 7) * scale;
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.fillStyle = 'rgba(60,90,120,0.22)';
  ctx.beginPath();
  ctx.ellipse(6, 7, width * 0.95, width * 0.38, 0, 0, TAU);
  ctx.fill();
  // három, egyre kisebb szint (alul a legnagyobb)
  for (let i = 0; i < 3; i++) {
    const t = i / 3;
    const y = -height * (0.16 + t * 0.62);
    const half = width * (1 - t * 0.46);
    ctx.fillStyle = i === 2 ? '#2f5a48' : i === 1 ? '#336351' : '#3a6e59';
    ctx.beginPath();
    ctx.moveTo(0, y - height * 0.34);
    ctx.lineTo(-half, y);
    ctx.lineTo(half, y);
    ctx.closePath();
    ctx.fill();
    // hó a szint tetején
    ctx.fillStyle = 'rgba(242,251,255,0.92)';
    ctx.beginPath();
    ctx.moveTo(0, y - height * 0.34);
    ctx.lineTo(-half * 0.5, y - height * 0.1);
    ctx.lineTo(half * 0.5, y - height * 0.1);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
};

/** Hóember felülnézetből: hógömb, kalap, sárgarépa orr. */
const drawSnowman = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  const pos = track.posAt(prop.s, prop.lateral);
  const scale = prop.scale ?? 1;
  const s = 1.05 * scale;
  ctx.save();
  ctx.translate(pos.x, pos.y);
  ctx.fillStyle = 'rgba(60,90,120,0.22)';
  ctx.beginPath();
  ctx.ellipse(4, 5, 14 * s, 6 * s, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = '#f6fbff';
  ctx.beginPath();
  ctx.arc(0, 0, 11 * s, 0, TAU);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(9 * s, -7 * s, 7 * s, 0, TAU);
  ctx.fill();
  ctx.fillStyle = '#2b3440';
  ctx.beginPath();
  ctx.arc(9 * s, -7 * s, 4 * s, 0, TAU);
  ctx.fill();
  ctx.fillStyle = '#e8843c';
  ctx.beginPath();
  ctx.moveTo(15 * s, -7 * s);
  ctx.lineTo(21 * s, -9 * s);
  ctx.lineTo(15 * s, -5 * s);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#9fb8cc';
  ctx.beginPath();
  ctx.arc(-3 * s, 2 * s, 1.4 * s, 0, TAU);
  ctx.fill();
  // seprűnyél
  ctx.strokeStyle = '#7a5a3a';
  ctx.lineWidth = 2 * s;
  ctx.beginPath();
  ctx.moveTo(-6 * s, 4 * s);
  ctx.lineTo(-16 * s, -8 * s);
  ctx.stroke();
  ctx.restore();
};

/** Egy prop kirajzolása (a pálya rétegében, a hívó dönti el, hova esik). */
const drawProp = (ctx: CanvasRenderingContext2D, track: Track, prop: PropDef): void => {
  switch (prop.kind) {
    case 'building':
      drawBuildings(ctx, track, prop);
      break;
    case 'tree':
      drawTree(ctx, track, prop);
      break;
    case 'lamp':
      drawLamp(ctx, track, prop);
      break;
    case 'kiosk':
      drawKiosk(ctx, track, prop);
      break;
    case 'bench':
      drawBench(ctx, track, prop);
      break;
    case 'cone':
      drawCone(ctx, track, prop);
      break;
    case 'parkedCar':
      drawParkedCar(ctx, track, prop);
      break;
    case 'sign':
      drawSign(ctx, track, prop);
      break;
    case 'hydrant':
    case 'bin':
      drawSmallProp(ctx, track, prop);
      break;
    case 'crosswalk':
      drawCrosswalk(ctx, track, prop);
      break;
    case 'arrow':
      drawArrow(ctx, track, prop);
      break;
    case 'grandstand':
      drawGrandstand(ctx, track, prop);
      break;
    case 'tyreWall':
      drawTyreWall(ctx, track, prop);
      break;
    case 'pitBuilding':
      drawPitBuilding(ctx, track, prop);
      break;
    case 'flagpole':
      drawFlagpole(ctx, track, prop);
      break;
    case 'cactus':
      drawCactus(ctx, track, prop);
      break;
    case 'pine':
      drawPine(ctx, track, prop);
      break;
    case 'snowman':
      drawSnowman(ctx, track, prop);
      break;
    case 'rock':
      drawRock(ctx, track, prop);
      break;
    case 'mineTower':
      drawMineTower(ctx, track, prop);
      break;
    case 'puddle': {
      const p = track.posAt(prop.s, prop.lateral);
      ctx.save();
      ctx.fillStyle = 'rgba(96,124,146,0.5)';
      ctx.beginPath();
      ctx.ellipse(p.x, p.y, 34, 18, 0, 0, TAU);
      ctx.fill();
      ctx.fillStyle = 'rgba(200,225,240,0.25)';
      ctx.beginPath();
      ctx.ellipse(p.x - 8, p.y - 4, 14, 6, 0, 0, TAU);
      ctx.fill();
      ctx.restore();
      break;
    }
    default:
      break;
  }
};

const drawWorld = (ctx: CanvasRenderingContext2D, track: Track, minX: number, minY: number, w: number, h: number): void => {
  const a = track.def.ambient;
  const roadHalf = track.def.roadHalfWidth;
  const kerb = track.def.kerbWidth;
  const corridor = roadHalf + kerb + track.def.sidewalkWidth;

  ctx.fillStyle = a.wall;
  ctx.fillRect(minX, minY, w, h);

  const theme = track.def.theme ?? 'city';
  if (theme === 'desert') drawDesertGround(ctx, minX, minY, w, h);
  if (theme === 'snow') drawSnowGround(ctx, minX, minY, w, h);
  if (theme === 'city' || theme === 'suburb') {
    drawCityBlocks(ctx, minX, minY, w, h, theme === 'suburb' ? SUBURB_ROOFS : CITY_ROOFS);
  }

  // belső terület: park / beton / gyep
  const infield = offsetLine(track, -(corridor + 70), 0, 1);
  ctx.beginPath();
  ctx.moveTo(infield[0].x, infield[0].y);
  for (let i = 1; i < infield.length; i++) ctx.lineTo(infield[i].x, infield[i].y);
  ctx.closePath();
  ctx.fillStyle =
    theme === 'suburb'
      ? '#5b5d5f'
      : theme === 'circuit'
        ? '#356b41'
        : theme === 'lake'
          ? '#4e7a4a'
          : theme === 'desert'
            ? '#c9a46b'
            : theme === 'snow'
              ? '#e8f3fb'
              : '#3c5a3f';
  ctx.fill();
  if (theme === 'circuit') drawMowingStripes(ctx, minX, minY, w, h);
  if (theme === 'snow') drawFrozenLake(ctx, infield);

  // várostömb-előtér: a járda és a házak közti sáv (csak városi témáknál)
  if (theme === 'city' || theme === 'suburb') {
    tracePath(ctx, track);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#6f6c66';
    ctx.lineWidth = (corridor + 78) * 2;
    ctx.stroke();
  }

  // Hungaroring: a pályán kívüli dekorációk ELŐBB kerülnek fel, mint az
  // aszfalt/járda, így a szűk kanyarokban nem lóghatnak be az úttestre.
  // A szerkesztőben készült pályákon a Hungaroring saját boxutcája és
  // kavicscsapdái nem értelmezhetők, ezért azokat kihagyjuk.
  if (theme === 'circuit') {
    if (!track.def.custom) {
      drawPitLane(ctx, track);
      drawPitComplex(ctx, track);
      drawGravelTraps(ctx, track);
      drawCircuitBoards(ctx, track);
    }
    const circuitProps = [...track.def.props].sort(
      (p, q) => track.posAt(p.s, p.lateral).y - track.posAt(q.s, q.lateral).y,
    );
    for (const prop of circuitProps) drawProp(ctx, track, prop);
  }

  // A sivatagi és a havas pályán is a járda/asphalt alatt vannak a díszletek,
  // hogy a szűk kanyarokban semmi ne lógjon az úttestre.
  if (theme === 'desert' || theme === 'snow') {
    const groundProps = [...track.def.props].sort(
      (p, q) => track.posAt(p.s, p.lateral).y - track.posAt(q.s, q.lateral).y,
    );
    for (const prop of groundProps) drawProp(ctx, track, prop);
  }

  // járda-sáv
  tracePath(ctx, track);
  ctx.strokeStyle = a.sidewalk;
  ctx.lineWidth = corridor * 2;
  ctx.stroke();

  // park-zöld a belső íven (témánként más szakaszokon)
  const zones =
    theme === 'suburb'
      ? [[0.12, 0.28], [0.62, 0.74]]
      : theme === 'lake'
        ? [[0.04, 0.96]]
        : theme === 'circuit' || theme === 'desert' || theme === 'snow'
          ? []
          : PARK_ZONES;
  for (const [from, to] of zones) {
    polyline(ctx, offsetLine(track, -192, from, to));
    ctx.strokeStyle = theme === 'suburb' ? '#6a6c6b' : theme === 'lake' ? '#5f8f55' : '#3f5c42';
    ctx.lineWidth = 132;
    ctx.stroke();
    polyline(ctx, offsetLine(track, -132, from, to));
    ctx.strokeStyle = 'rgba(150,180,130,0.3)';
    ctx.lineWidth = 10;
    ctx.stroke();
  }

  // szegélykő
  tracePath(ctx, track);
  ctx.strokeStyle = a.kerb;
  ctx.lineWidth = (roadHalf + kerb) * 2;
  ctx.stroke();
  // aszfalt
  tracePath(ctx, track);
  ctx.strokeStyle = a.asphalt;
  ctx.lineWidth = roadHalf * 2;
  ctx.stroke();

  // aszfalt-szemcse
  for (let i = 0; i < 2200; i++) {
    const s = hash01(i * 7 + 1);
    const lat = (hash01(i * 13 + 5) * 2 - 1) * (roadHalf - 6);
    const p = track.posAt(s, lat);
    const size = 1 + hash01(i * 17 + 3) * 2.5;
    ctx.fillStyle = hash01(i) > 0.5 ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.05)';
    ctx.fillRect(p.x, p.y, size, size);
  }

  // sávfestés
  ctx.save();
  ctx.setLineDash([30, 26]);
  ctx.lineWidth = 3;
  ctx.strokeStyle = a.lane;
  for (const lateral of [-48, 0, 48]) {
    polyline(ctx, offsetLine(track, lateral));
    ctx.stroke();
  }
  ctx.restore();

  // külső folytonos vonal
  ctx.save();
  ctx.lineWidth = 3;
  ctx.strokeStyle = 'rgba(244,241,232,0.4)';
  for (const lateral of [-roadHalf + 6, roadHalf - 6]) {
    polyline(ctx, offsetLine(track, lateral));
    ctx.stroke();
  }
  ctx.restore();

  // szegélykő csíkozás
  ctx.save();
  ctx.setLineDash([26, 26]);
  ctx.lineWidth = kerb * 0.8;
  ctx.strokeStyle = 'rgba(244,241,232,0.92)';
  for (const lateral of [-roadHalf - kerb / 2, roadHalf + kerb / 2]) {
    polyline(ctx, offsetLine(track, lateral));
    ctx.stroke();
  }
  ctx.restore();

  drawPotholes(ctx, track);

  if (theme === 'city') drawTram(ctx, track);
  drawStartLine(ctx, track);
  if (theme === 'circuit') {
    drawGridSlots(ctx, track);
    if (!track.def.custom) drawStartGantry(ctx, track);
  }

  // A circuit, a sivatag és a havas pálya díszletei már a járda/asphalt alatt vannak;
  // a többi témánál marad a hagyományos, y szerint rendezett prop-réteg.
  if (theme !== 'circuit' && theme !== 'desert' && theme !== 'snow') {
    const sorted = [...track.def.props].sort(
      (p, q) => track.posAt(p.s, p.lateral).y - track.posAt(q.s, q.lateral).y,
    );
    for (const prop of sorted) drawProp(ctx, track, prop);
  }
};

export function buildTrackLayer(track: Track): TrackLayer {
  const pad = 620;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const s of track.samples) {
    minX = Math.min(minX, s.pos.x);
    minY = Math.min(minY, s.pos.y);
    maxX = Math.max(maxX, s.pos.x);
    maxY = Math.max(maxY, s.pos.y);
  }
  minX -= pad;
  minY -= pad;
  maxX += pad;
  maxY += pad;

  const worldW = maxX - minX;
  const worldH = maxY - minY;
  const maxDim = 4096;
  const scale = Math.min(1, maxDim / Math.max(worldW, worldH));

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.ceil(worldW * scale));
  canvas.height = Math.max(1, Math.ceil(worldH * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Nem sikerült a pálya-réteg canvasát létrehozni.');

  ctx.setTransform(scale, 0, 0, scale, -minX * scale, -minY * scale);
  drawWorld(ctx, track, minX, minY, worldW, worldH);

  return {
    canvas,
    minX,
    minY,
    width: canvas.width / scale,
    height: canvas.height / scale,
    scale,
  };
}

export function drawTrackLayer(ctx: CanvasRenderingContext2D, layer: TrackLayer, _cam: CameraView): void {
  ctx.drawImage(layer.canvas, layer.minX, layer.minY, layer.width, layer.height);
}

export function drawTrackDynamic(ctx: CanvasRenderingContext2D, track: Track, cam: CameraView, time: number): void {
  for (const pad of track.def.boostPads) {
    const startS = pad.s * track.totalLength;
    const center = track.posAt(pad.s, pad.lateral);
    if (
      center.x < cam.bounds.minX - 200 ||
      center.x > cam.bounds.maxX + 200 ||
      center.y < cam.bounds.minY - 200 ||
      center.y > cam.bounds.maxY + 200
    ) {
      continue;
    }
    // a pad alapsávja: ettől látszik, hogy ez egy felület, nem festék
    const band: Vec2[] = [];
    const edgeL: Vec2[] = [];
    const edgeR: Vec2[] = [];
    for (let d = -6; d <= pad.length + 6; d += 18) {
      const prog = track.progressOfS(startS + d);
      band.push(track.posAt(prog, pad.lateral));
      edgeL.push(track.posAt(prog, pad.lateral - pad.width / 2 + 5));
      edgeR.push(track.posAt(prog, pad.lateral + pad.width / 2 - 5));
    }
    ctx.save();
    polyline(ctx, band);
    ctx.lineCap = 'butt';
    ctx.strokeStyle = 'rgba(255,132,40,0.26)';
    ctx.lineWidth = pad.width;
    ctx.stroke();
    ctx.lineCap = 'round';
    ctx.lineWidth = 3;
    for (const edge of [edgeL, edgeR]) {
      polyline(ctx, edge);
      ctx.strokeStyle = 'rgba(255,200,87,0.75)';
      ctx.stroke();
    }
    polyline(ctx, band);
    ctx.strokeStyle = 'rgba(255,220,140,0.5)';
    ctx.lineWidth = 3;
    ctx.setLineDash([12, 14]);
    ctx.stroke();
    ctx.restore();

    const count = Math.floor(pad.length / 42);
    for (let i = 0; i < count; i++) {
      const offset = (i * 42 + ((time * 90) % 42)) % pad.length;
      const progress = track.progressOfS(startS + offset);
      const p = track.posAt(progress, pad.lateral);
      const t = track.tangentAt(progress);
      const fade = 1 - offset / pad.length;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(Math.atan2(t.y, t.x));
      ctx.strokeStyle = `rgba(255,200,87,${(0.25 + fade * 0.6).toFixed(3)})`;
      ctx.lineWidth = 9;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(-14, -26);
      ctx.lineTo(6, 0);
      ctx.lineTo(-14, 26);
      ctx.stroke();
      ctx.restore();
    }
  }
}

interface MinimapLayout {
  scale: number;
  offsetX: number;
  offsetY: number;
}

const minimapLayout = (track: Track, size: number): MinimapLayout => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const s of track.samples) {
    minX = Math.min(minX, s.pos.x);
    minY = Math.min(minY, s.pos.y);
    maxX = Math.max(maxX, s.pos.x);
    maxY = Math.max(maxY, s.pos.y);
  }
  const pad = 18;
  const scale = Math.min((size - pad * 2) / (maxX - minX), (size - pad * 2) / (maxY - minY));
  return {
    scale,
    offsetX: (size - (maxX - minX) * scale) / 2 - minX * scale,
    offsetY: (size - (maxY - minY) * scale) / 2 - minY * scale,
  };
};

/** A pálya adott pontja a minimap canvasán, 0..1 arányban. */
export function minimapPoint(track: Track, progress: number, size: number): { x: number; y: number } {
  const layout = minimapLayout(track, size);
  const p = track.posAt(progress, 0);
  return {
    x: (layout.offsetX + p.x * layout.scale) / size,
    y: (layout.offsetY + p.y * layout.scale) / size,
  };
}

export function buildMinimap(track: Track, size: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  const layout = minimapLayout(track, size);
  ctx.translate(layout.offsetX, layout.offsetY);
  ctx.scale(layout.scale, layout.scale);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  tracePath(ctx, track);
  ctx.strokeStyle = 'rgba(10,12,18,0.9)';
  ctx.lineWidth = 100;
  ctx.stroke();

  tracePath(ctx, track);
  ctx.strokeStyle = '#f4f1e8';
  ctx.lineWidth = 62;
  ctx.stroke();

  tracePath(ctx, track);
  ctx.strokeStyle = 'rgba(255,200,87,0.55)';
  ctx.lineWidth = 16;
  ctx.setLineDash([90, 90]);
  ctx.stroke();
  ctx.setLineDash([]);

  const start = track.posAt(track.startS / track.totalLength, 0);
  ctx.fillStyle = '#ff5c4d';
  ctx.beginPath();
  ctx.arc(start.x, start.y, 58, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = '#f4f1e8';
  ctx.lineWidth = 20;
  ctx.stroke();

  return canvas;
}
